<template>
  <div v-if="isShowLoadingAnimation">
    <loading-screen></loading-screen>
  </div>
  <div v-else class="thanks-page-content text-center mx-auto">
    <div class="thanks-page-info">
      <div class="thank-you-text">
        <p class="font-weight-bold fs-24 mb-0">
          Thank you {{ vehicleInfo.customerName }} for your enquiry on AutoFlip.
        </p>
      </div>
      <div v-if="isShowValuation" class="estimated-value">
        <p class="mb-2 font-weight-bold title">ESTIMATED MARKET GUIDE</p>
        <div
          class="
            autoflip-price
            d-flex
            justify-content-center
            align-items-center
            font-weight-bold
          "
          :class="hasGotPrice ? '' : 'no-price'"
        >
          <span v-if="hasGotPrice" class="fs-20">
            {{ `${fromPrice} - ${toPrice}` }}*
          </span>
          <span v-else class="no-valuation">
            We can't provide an accurate price guidance right now
          </span>
        </div>
        <div class="market-guide-disclaimer">
          <p class="text-center">This is a guide only. Actual sale price may vary.  </p>
        </div>
        <div class="redbook-logo">
          <p class="text-center">Powered by</p>
          <img v-if="isMobile" :src="require('@/assets/images/logo_redbook_mobile.png')" />
          <img v-else :src="require('@/assets/images/logo_redbook_desktop.png')" />
        </div>
      </div>
      <div class="complete-your-listing pt-4">
        <p class="section-header font-weight-bold fs-24">COMPLETE YOUR LISTING</p>
        <p class="font-weight-thin mb-4">To get your car listed faster, finish adding more details.</p>
        <button type="button" @click="handleAddDetails">
          <span class="font-weight-regular">ADD MORE DETAILS</span>
        </button>
      </div>
    </div>
    <div class="thanks-page-question mb-4">
      <p class="section-header font-weight-bold fs-24">WHAT TO EXPECT NEXT?</p>
      <ul class="desc">
        <li
          v-for="(item, index) in whatToExpectNextContent"
          :key="index"
          class="font-weight-thin"
        >
          {{ item }}
        </li>
      </ul>
    </div>
    <how-we-work></how-we-work>
    <FAQs></FAQs>
    <div v-if="isShowValuation" class="pricing-disclaimer font-weight-thin">
      <p>
        * The price guide is created using independent market data. 
        It is indicative only and relies on the vehicle information provided at the time of submission. 
        Details such as vehicle specification, number of kilometres, and condition of vehicle all impact the price guide. 
        The final sale price may differ and will depend on market conditions and demand from our dealer network. 
        AutoFlip is not responsible for any errors provided or omissions of information.
      </p>
    </div>
  </div>
</template>

<script>
import HowWeWork from "@/components/thanks-page/HowWeWork";
import FAQs from "@/components/thanks-page/FAQs";
import LoadingScreen from "@/components/thanks-page/LoadingScreen";
import customerDetailService from "@/services/customer-detail";
import { API_ENDPOINT } from "@/config";
import { EventBus } from '@/helper/event-bus';

export default {
  name: "Index",
  data() {
    return {
      vehicleInfo: { ...this.$store.getters.curVehicleInfo },
      isMobile: true,
      valuation: {},
      isShowValuation: false,
      isShowLoadingAnimation: false,
      apiEndPoint: API_ENDPOINT,
    };
  },
  computed: {
    mobileMediaQuery: function() {
      return window.matchMedia("(min-width: 1000px)");
    },
    isNotSelling: function() {
      return this.vehicleInfo.sell === "I'm not selling";
    },
    fromPrice() {
      return "$" + new Intl.NumberFormat().format(this.valuation.from);
    },
    toPrice() {
      return "$" + new Intl.NumberFormat().format(this.valuation.to);
    },
    discretePrice() {
      return "$" + new Intl.NumberFormat().format(this.valuation.discreteValue);
    },
    hasGotPrice: function() {
      return this.valuation.from > 0 && this.valuation.to > 0;
    },
    whatToExpectNextContent: function() {
      return !this.isNotSelling
        ? [
            "Before your car is officially listed for sale, a member of our customer service team will contact you within the next business day.",
            "They'll confirm a few details, advise you on valuing your car, answer any questions and then go to work to get you the best price, fast.",
            "In the meantime, you can continue updating your details by clicking the link provided in the confirmation email you'll receive shortly.",
          ]
        : [
            "A friendly team member from AutoFlip may call you to check if they can assist with anything for your car.",
            "If you change your mind and decide to sell your car, continue updating your details by clicking the link provided in the confirmation email you'll receive shortly.",
          ];
    },
  },
  methods: {
    // goBack() {
    //   this.$router.push({name: 'vehicle-info'})
    // }
    async handleAddDetails() {
      try {
        const response = await customerDetailService.getAppraisalId(
          this.$store.getters.carDetailRecordId,
          { disableToast: true }
        );
        const appraisalId = response?.data?.appraisalId;
        const hubspotLeadId = response?.data?.hubspotLeadId;
        if (
          ["autoplay_upload_success", "hubspot_upload_success"].includes(
            response.data.uploadStatus
          ) &&
          (appraisalId || hubspotLeadId)
        ) {
          await customerDetailService.updatePSPDataLogsWithAction({
            appraisalId,
            hubspotLeadId,
            loggedAction: "MyCar Direct CTA",
          });
          this.redirectToMyCarPage(appraisalId, hubspotLeadId);
        } else {
          localStorage.setItem("customerEmail", this.vehicleInfo.customerEmail);
          this.isShowLoadingAnimation = true;
          EventBus.$emit("show-loading-screen");
        }
      } catch (error) {
        console.error("Failed to fetch appraisal ID:", error);
        this.isShowLoadingAnimation = true;
        EventBus.$emit('show-loading-screen');
      }
    },
    getMyCarPage() {
      let redirectBase = '';
      if (this.apiEndPoint.includes('psp-test')) {
        redirectBase = 'https://psp-test.idomitech.net/inv10';
      } else if (this.apiEndPoint.includes('psp-staging')) {
        redirectBase = 'https://psp-staging.idomitech.net/inv10';
      } else if (this.apiEndPoint.includes('autoflip.com.au')) {
        redirectBase = 'https://mycar.autoflip.com.au';
      }

      return redirectBase;
    },
    redirectToMyCarPage(appraisalId, hubspotLeadId) {
      const generatedTime = new Date();
      const platformId = appraisalId || hubspotLeadId;
      const redirectUrl = `${this.getMyCarPage()}?universal_login=true&appraisalId=${platformId}${
        this.locid === 2 ? "&locid=2" : ""
      }&generated_time=${generatedTime.getTime()}&utm_source=direct_mycar&email=${this.vehicleInfo.customerEmail}`;

      window.location.href = redirectUrl;
    },
  },
  components: {
    HowWeWork,
    FAQs,
    LoadingScreen,
  },
  mounted() {
    // Check mobile screen
    this.mobileMediaQuery.addListener((e) => {
      if (e.matches) {
        this.isMobile = false;
      } else {
        this.isMobile = true;
      }
    });

    this.isMobile = !this.mobileMediaQuery.matches;
    this.valuation = JSON.parse(sessionStorage.getItem('valuation'))
    sessionStorage.removeItem('valuation')

    const valuationToggle = localStorage.getItem('isShowValuation');
    if (valuationToggle && valuationToggle === 'true') this.isShowValuation = true;
    localStorage.removeItem('isShowValuation');
  },
};
</script>

<style lang="scss">
.thanks-page-content {
  color: #404040;
  
  .thanks-page-info {
    background-color: #f9fafb;
    padding: 20px;
  }

  .estimated-value {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin: 0 auto;
    padding-top: 30px;
    width: 366px;
    
    .title {
      font-size: 24px;
      line-height: 23px;
      color: #1F8252;
      margin-bottom: 15px !important;
    }

    .autoflip-price {
      font-family: Avenir Next LT W05 Demi;
      min-width: 340px;
      height: 71px;
      margin: 5px auto 5px;
      border-radius: 30px;
      border: 1px solid #414141;
      width: 90%;
    }

    .no-valuation {
      font-size: 16px;
      padding: 10px;
    }

    .btn-loading {
      font-size: 16px;
      width: 24px;
      height: 24px;
    }

    .market-guide-disclaimer {
      font-size: 12px;
      line-height: 24px;
    }

    .redbook-logo {
      p {
        margin-bottom: 0.5rem;
      }
      img {
        margin-bottom: 1rem;
      }
    }
  }

  .thanks-page-question {
    .section-header {
      color: #1f8252;
      text-transform: uppercase;
      margin: 1rem 0;
    }
    .desc {
      padding-inline-start: 35px;
      padding-right: 20px;
      li {
        text-align: left;
      }
    }
  }

  .complete-your-listing {
    .section-header {
      color: #1f8252;
      text-transform: uppercase;
      margin: 1rem 0;
    }

    button {
      width: 100%;
      max-width: 722px;
      height: 46px;
      border-radius: 6px;
      padding: 11px 24px;
      background-color: #1F8252;
      border: none;
      outline: none;
      color: white;
      font-size: 16px;
    }
  }

  .lean-info {
    padding-bottom: 30px;
  }
  .pricing-disclaimer {
    text-align: left;
    padding: 5px 16px 30px;
    color: #989898;
    font-size: 10px;
    line-height: 16px;
    min-width: 365px;
    
    p {
      margin: unset;
    }
  }
}

@media screen and (min-width: 768px) {
  .thanks-page-content {
    .thank-you-text p {
      margin: 0 auto;
      max-width: 800px;
    }
    .main-content {
      padding-bottom: 30px !important;
      .vehicle {
        max-width: 980px;
        display: flex;
        justify-content: space-between;
      }
      .text-note {
        max-width: 515px !important;
        margin-top: 17px !important;
        padding: 0 !important;
      }
      .item {
        padding: 0px 22px !important;
      }
    }
    .lean-info {
      margin: 0;
      width: 100%;
      max-width: unset;
      .title-info {
        padding-top: 20px;
      }
    }
    .pricing-disclaimer {
      padding: 10px 100px !important;
      
      p {
        width: 70% !important;
        margin: 0 auto !important;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .content-detail-tittle{
    font-size: 20px !important;
  }

  .main-content-title {
    font-size: 20px !important;
  }

  .car-note {
    text-align: left !important;
  }
  
  .autoflip-price {
    max-width: 303px !important;

    .no-valuation {
      font-size: 20px !important;
    }
  }
  
  .no-price {
    max-width:303px !important;
  }
}

@media screen and (min-width: 768px) and (max-width: 1000px) {
  .title-content {
    .top-head {
      margin-bottom: 0rem !important;
      font-size: 22px !important;
      padding-top: 12px !important;
    }
    .desc {
      margin-top: 2px !important;
      .bottom-head {
        font-size: 22px !important;
        margin-bottom: 10px !important;
      }
      .desc-content {
        font-size: 14px !important;
      }
    }
  }
}

@media screen and (max-width: 450px) and (orientation: portrait){
  .top-head {
    padding: 0 16px;
  }
}
@media screen and (min-width: 768px) and (max-width: 991px) {
  .main-content {
    .vehicle-non-slide {
      max-width: 600px !important;
    }
    .vehicle-slide {
      max-width: 625px !important;
    }
    .vehicle {
      display: block !important;
      justify-content: unset !important;
      .vehicle-condition {
        margin: 0 auto !important;
        width: 100% !important;
        max-width: 515px !important;
        .vehicle-condition-wrapper {
          flex-direction: column !important;
          .vehicle-condition-item {
            margin: 0 auto !important;
            transform: scale(1.1);
          }
        }
      }
      .vehicle-detail {
        margin: 0 auto !important;
        width: 100% !important;
        max-width: 515px !important;
        margin-top: 50px !important;
      }
    }
  }
}
@media screen and (min-width: 992px) and (max-width: 1199px) {
  .vehicle {
    .vehicle-condition {
      margin-left: 5px !important;
    }
    .vehicle-detail {
      margin-right: 5px !important;
    }
  }
}
@media screen and(min-width: 900px) {
  .thanks-page-content {
    .thanks-page-question {
      .desc {
        max-width: 620px;
        margin: 0 auto;
      }
    }
  }
}
@media screen and(min-width: 990px) {
  .thanks-page-content {
    .main-content {
        .text-note {
          margin-left: 0 !important;
        }
      }
  }
}
@media screen and(min-width: 1200px) {
  .thanks-page-content {
    padding: 0 10px;
    .thank-you-text p {
      font-size: 27px !important;
    }
    .thanks-page-question {
      .desc {
        max-width: 800px;
      }
    }
    .lean-info .type-info {
      max-width: 1050px !important;
      margin: 0 auto;
    }
  }
}
@media screen and (min-width: 1024px) {
  .estimated-value {
    padding-right: 30px;
    
    p {
      margin-bottom: 1rem !important;
    }

    .no-valuation {
      text-align: center !important;
    }

    .redbook-logo {
      p {
        margin-bottom: 0.5rem !important;
      }
    }
  }
  .pricing-disclaimer {
    padding: 25px 150px !important;
    font-size: 14px !important;
    line-height: 21px !important;
    text-align: center !important;
  }
}
@media screen and (min-width: 1024px) and (max-width: 1300px) {
  .pricing-disclaimer {
    p {
      width: 80% !important;
    }
  }
}
</style>
